import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Keyboard } from 'swiper/modules';
import type { Swiper as SwiperInstance } from 'swiper';

import 'swiper/css';

import { LANDING_SLIDES } from '../landingContent';

/**
 * Screenshots of the operator dashboard, captured from a seeded demonstration
 * network (ev-backend/scripts/seed-demo.ts, then
 * tests/e2e/capture-landing.spec.ts). The screens are the real product; the
 * network, drivers and figures in them are synthetic, which is what
 * `slider.dataNote` below tells the reader. Swiper is already the app's
 * carousel (see components/features/OfferSlider), so this reuses it — but with
 * its own controls and the caption outside the slides.
 *
 * Both choices are deliberate. The captions run to different lengths and the
 * screenshots size themselves only once loaded, so leaving them inside the
 * slides means Swiper measures a height that is wrong by the time anyone reads
 * it. And the cabinet's OfferSlider vendors a copy of Swiper's CSS whose
 * unscoped `.swiper-pagination-*` rules would style these dots too. Keeping
 * slides to a fixed-ratio image, and captions and controls in normal flow
 * underneath, sidesteps both.
 */
export const DashboardSlider = () => {
  const { t } = useTranslation();
  const [swiper, setSwiper] = useState<SwiperInstance | null>(null);
  const [active, setActive] = useState(0);

  const activeSlide = LANDING_SLIDES[active] ?? LANDING_SLIDES[0];

  return (
    <div className="lp-slider">
      <Swiper
        modules={[Autoplay, Keyboard]}
        slidesPerView={1}
        spaceBetween={24}
        loop
        keyboard={{ enabled: true }}
        autoplay={{ delay: 6000, disableOnInteraction: true }}
        onSwiper={setSwiper}
        onSlideChange={(instance) => setActive(instance.realIndex)}
      >
        {LANDING_SLIDES.map((slide, index) => (
          <SwiperSlide key={slide.id}>
            <div className="lp-slide__frame">
              <span className="lp-slide__chrome" aria-hidden="true">
                <i />
                <i />
                <i />
              </span>
              <img
                className="lp-slide__shot"
                src={slide.image}
                alt={t(`landing.slider.${slide.id}.title`)}
                loading={index === 0 ? 'eager' : 'lazy'}
                width={1600}
                height={1000}
              />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      <div className="lp-slider__footer">
        <div className="lp-slide__meta" aria-live="polite">
          <span className="lp-slide__title">
            {t(`landing.slider.${activeSlide.id}.title`)}
          </span>
          <span className="lp-slide__caption">
            {t(`landing.slider.${activeSlide.id}.caption`)}
          </span>
        </div>

        <div className="lp-slider__controls">
          <ol className="lp-dots">
            {LANDING_SLIDES.map((slide, index) => (
              <li key={slide.id}>
                <button
                  type="button"
                  className="lp-dots__btn"
                  aria-current={index === active}
                  aria-label={t(`landing.slider.${slide.id}.title`)}
                  onClick={() => swiper?.slideToLoop(index)}
                />
              </li>
            ))}
          </ol>

          <div className="lp-slider__arrows">
            <button
              type="button"
              className="lp-arrow"
              aria-label={t('landing.slider.previous')}
              onClick={() => swiper?.slidePrev()}
            >
              ‹
            </button>
            <button
              type="button"
              className="lp-arrow"
              aria-label={t('landing.slider.next')}
              onClick={() => swiper?.slideNext()}
            >
              ›
            </button>
          </div>
        </div>
      </div>

      <p className="lp-slider__note">{t('landing.slider.dataNote')}</p>
    </div>
  );
};

export default DashboardSlider;
